import { queryOptions, useMutation } from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';

import type {
  AspirationByDonorDateSiteResponse,
  AspirationChutesideResult,
  AspirationDataEntryRequest,
  AspirationHistoryResponse,
  AspirationsTodayResponse,
  DonorScheduleResponse,
  FertilizationDataResponse,
  PregnantStatusParams,
  ProcedureHistoryResponse,
  QcDataBulkRequest,
  QcDataChutesideResponse,
  QcDataRequest,
  TempQcInfoResponse,
} from './types';

export type {
  AspirationByDonorDateSiteResponse,
  AspirationChutesideResult,
  AspirationDataEntryRequest,
  AspirationHistoryResponse,
  AspirationsTodayResponse,
  DonorScheduleResponse,
  FertilizationDataResponse,
  PregnantStatusParams,
  ProcedureHistoryResponse,
  QcDataBulkRequest,
  QcDataChutesideResponse,
  QcDataRequest,
  TempQcInfoResponse,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(token: AccessToken): HeadersInit {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchGet<T>(
  token: AccessToken,
  path: string,
  params?: URLSearchParams,
): Promise<T> {
  const url = params
    ? `${getBaseUrl()}/api/v1/aspiration/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/v1/aspiration/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchMutation<T>(
  token: AccessToken,
  path: string,
  method: 'POST' | 'PUT',
  body?: unknown,
  params?: URLSearchParams,
): Promise<T> {
  const baseUrl = `${getBaseUrl()}/api/v1/aspiration/${path}`;
  const url = params ? `${baseUrl}?${params.toString()}` : baseUrl;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(token),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchTodaysAspirations(
  token: AccessToken,
  params: {
    date: string;
    siteId: string;
    vetId?: string;
    techId?: string;
    type: number;
    location?: string;
  },
): Promise<AspirationsTodayResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('siteId', params.siteId);
  if (params.vetId) searchParams.set('vetId', params.vetId);
  if (params.techId) searchParams.set('techId', params.techId);
  searchParams.set('type', String(params.type));
  if (params.location) searchParams.set('location', params.location);
  return fetchGet<AspirationsTodayResponse[]>(token, 'today', searchParams);
}

async function fetchAspirationByDonorDateSite(
  token: AccessToken,
  params: {
    donorId: string;
    date: string;
    siteId: string;
  },
): Promise<AspirationByDonorDateSiteResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorId', params.donorId);
  searchParams.set('date', params.date);
  searchParams.set('siteId', params.siteId);
  return fetchGet<AspirationByDonorDateSiteResponse[]>(
    token,
    'by-donor-date-site',
    searchParams,
  );
}

async function fetchTempQcInfo(
  token: AccessToken,
  params: {
    chuteNumber: number;
    siteId: string;
    aspirationDate: string;
    location?: string;
  },
): Promise<TempQcInfoResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('chuteNumber', String(params.chuteNumber));
  searchParams.set('siteId', params.siteId);
  searchParams.set('aspirationDate', params.aspirationDate);
  if (params.location) searchParams.set('location', params.location);
  return fetchGet<TempQcInfoResponse>(token, 'temp-qc-info', searchParams);
}

async function fetchProceduresByDonorId(
  token: AccessToken,
  params: {
    donorId: string;
    ranch?: string;
  },
): Promise<ProcedureHistoryResponse[]> {
  const searchParams = new URLSearchParams();
  if (params.ranch) searchParams.set('ranch', params.ranch);
  const path = `procedures/${params.donorId}`;
  return fetchGet<ProcedureHistoryResponse[]>(
    token,
    path,
    searchParams.toString() ? searchParams : undefined,
  );
}

async function fetchQcDataChuteside(
  token: AccessToken,
  params: {
    date: string;
    siteId: string;
    location: string;
  },
): Promise<QcDataChutesideResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('siteId', params.siteId);
  searchParams.set('location', params.location);
  return fetchGet<QcDataChutesideResponse>(token, 'qc-data', searchParams);
}

async function fetchFertDataByDonorId(
  token: AccessToken,
  params: {
    donorId: string;
    ranch?: string;
  },
): Promise<FertilizationDataResponse[]> {
  const searchParams = new URLSearchParams();
  if (params.ranch) searchParams.set('ranch', params.ranch);
  const path = `fert-data/${params.donorId}`;
  return fetchGet<FertilizationDataResponse[]>(
    token,
    path,
    searchParams.toString() ? searchParams : undefined,
  );
}

async function fetchAspirationHistory(
  token: AccessToken,
  params: {
    donorId: string;
    aspirationDate: string;
    ranch?: string;
  },
): Promise<AspirationHistoryResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  if (params.ranch) searchParams.set('ranch', params.ranch);
  const path = `history/${params.donorId}`;
  return fetchGet<AspirationHistoryResponse[]>(token, path, searchParams);
}

async function fetchDonorSchedule(
  token: AccessToken,
  params: {
    donorId: string;
    aspirationDate: string;
    ranch?: string;
  },
): Promise<DonorScheduleResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  if (params.ranch) searchParams.set('ranch', params.ranch);
  const path = `donor-schedule/${params.donorId}`;
  return fetchGet<DonorScheduleResponse[]>(token, path, searchParams);
}

export const todaysAspirationsOptions = (
  token: AccessToken,
  params: {
    date: string;
    siteId: string;
    vetId?: string;
    techId?: string;
    type: number;
    location?: string;
  },
) =>
  queryOptions({
    queryKey: ['aspiration', 'today', params],
    enabled: !!token,
    async queryFn() {
      return fetchTodaysAspirations(token, params);
    },
  });

export const aspirationByDonorDateSiteOptions = (
  token: AccessToken,
  params: {
    donorId: string;
    date: string;
    siteId: string;
  },
) =>
  queryOptions({
    queryKey: ['aspiration', 'by-donor-date-site', params],
    enabled: !!token,
    async queryFn() {
      return fetchAspirationByDonorDateSite(token, params);
    },
  });

export const tempQcInfoOptions = (
  token: AccessToken,
  params: {
    chuteNumber: number;
    siteId: string;
    aspirationDate: string;
    location?: string;
  },
) =>
  queryOptions({
    queryKey: ['aspiration', 'temp-qc-info', params],
    enabled: !!token,
    async queryFn() {
      return fetchTempQcInfo(token, params);
    },
  });

export const proceduresByDonorIdOptions = (
  token: AccessToken,
  params: {
    donorId: string;
    ranch?: string;
  },
) =>
  queryOptions({
    queryKey: ['aspiration', 'procedures', params],
    enabled: !!token,
    async queryFn() {
      return fetchProceduresByDonorId(token, params);
    },
  });

export const qcDataChutesideOptions = (
  token: AccessToken,
  params: {
    date: string;
    siteId: string;
    location: string;
  },
) =>
  queryOptions({
    queryKey: ['aspiration', 'qc-data', params],
    enabled: !!token,
    async queryFn() {
      return fetchQcDataChuteside(token, params);
    },
  });

export const fertDataByDonorIdOptions = (
  token: AccessToken,
  params: {
    donorId: string;
    ranch?: string;
  },
) =>
  queryOptions({
    queryKey: ['aspiration', 'fert-data', params],
    enabled: !!token,
    async queryFn() {
      return fetchFertDataByDonorId(token, params);
    },
  });

export const aspirationHistoryOptions = (
  token: AccessToken,
  params: {
    donorId: string;
    aspirationDate: string;
    ranch?: string;
  },
) =>
  queryOptions({
    queryKey: ['aspiration', 'history', params],
    enabled: !!token,
    async queryFn() {
      return fetchAspirationHistory(token, params);
    },
  });

export const donorScheduleOptions = (
  token: AccessToken,
  params: {
    donorId: string;
    aspirationDate: string;
    ranch?: string;
  },
) =>
  queryOptions({
    queryKey: ['aspiration', 'donor-schedule', params],
    enabled: !!token,
    async queryFn() {
      return fetchDonorSchedule(token, params);
    },
  });

async function updateAspirationChutesideFn(
  token: AccessToken,
  input: AspirationDataEntryRequest,
  isReturn: string,
): Promise<AspirationChutesideResult> {
  const params = new URLSearchParams();
  params.set('isReturn', isReturn);
  return fetchMutation<AspirationChutesideResult>(
    token,
    'chuteside',
    'PUT',
    input,
    params,
  );
}

async function updateQcDataFn(
  token: AccessToken,
  input: QcDataRequest,
): Promise<boolean> {
  return fetchMutation<boolean>(token, 'qc-data', 'PUT', input);
}

async function updateDonorLastBredFn(
  token: AccessToken,
  params: {
    donorId: string;
    lastBredDate: string;
  },
): Promise<boolean> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorId', params.donorId);
  searchParams.set('lastBredDate', params.lastBredDate);
  return fetchMutation<boolean>(
    token,
    'donor-last-bred',
    'PUT',
    undefined,
    searchParams,
  );
}

async function updateQcDataBulkFn(
  token: AccessToken,
  input: QcDataBulkRequest,
  params: { aspirationDate: string; siteId: string; location: string },
): Promise<boolean> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  searchParams.set('location', params.location);
  return fetchMutation<boolean>(
    token,
    'qc-data-bulk',
    'PUT',
    input,
    searchParams,
  );
}

async function addPregnantStatusFn(
  token: AccessToken,
  params: PregnantStatusParams,
): Promise<unknown> {
  const searchParams = new URLSearchParams();
  searchParams.set('pregnantValue', String(params.pregnantValue));
  searchParams.set('donorId', params.donorId);
  searchParams.set('opuTechId', params.opuTechId);
  searchParams.set('chuteTechId', params.chuteTechId);
  return fetchMutation<unknown>(
    token,
    'pregnant-status',
    'POST',
    undefined,
    searchParams,
  );
}

export function useUpdateAspirationChutesideMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: ({
      input,
      isReturn,
    }: {
      input: AspirationDataEntryRequest;
      isReturn: string;
    }) => updateAspirationChutesideFn(token, input, isReturn),
  });
}

export function useUpdateQcDataMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (input: QcDataRequest) => updateQcDataFn(token, input),
  });
}

export function useUpdateDonorLastBredMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: { donorId: string; lastBredDate: string }) =>
      updateDonorLastBredFn(token, params),
  });
}

export function useUpdateQcDataBulkMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: ({
      input,
      aspirationDate,
      siteId,
      location,
    }: {
      input: QcDataBulkRequest;
      aspirationDate: string;
      siteId: string;
      location: string;
    }) =>
      updateQcDataBulkFn(token, input, { aspirationDate, siteId, location }),
  });
}

export function useAddPregnantStatusMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: PregnantStatusParams) =>
      addPregnantStatusFn(token, params),
  });
}
